import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, STATUS_LABELS, API } from "@/lib/api";
import { toast } from "sonner";
import { Package, CheckCircle2, RefreshCw, Mail, Camera, MessageSquare, Tag } from "lucide-react";

const STATUS_OPTIONS = ["quote_accepted", "received", "parts_arrived", "in_repair", "ready_for_pickup", "completed", "cancelled"];

export default function Admin() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all");

  const load = async () => {
    const [b, s] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/stats")]);
    setBookings(b.data); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = bookings.filter((b) => {
    if (tab === "all") return true;
    if (tab === "parts_queue") return b.parts_paid && !b.parts_ordered;
    if (tab === "active") return ["received", "parts_arrived", "in_repair"].includes(b.status);
    if (tab === "ready") return b.status === "ready_for_pickup";
    return true;
  });

  const markOrdered = async (id) => {
    try { await api.post(`/admin/bookings/${id}/mark-parts-ordered`); toast.success("Parts marked as ordered"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/status`, { status });
      if (status === "ready_for_pickup") {
        const parts = [];
        if (data.email_sent) parts.push("email sent");
        else if (data.email_skipped) parts.push("email already sent");
        else parts.push("email failed");
        if (data.sms_sent) parts.push("SMS sent");
        else if (data.sms_skipped) parts.push("SMS already sent");
        else if (data.sms_skipped_reason === "twilio_not_configured") parts.push("SMS skipped (Twilio not configured)");
        else if (data.sms_error) parts.push(`SMS failed: ${data.sms_error}`);
        const message = `Pickup: ${parts.join(" · ")}`;
        const allGood = (data.email_sent || data.email_skipped) && (data.sms_sent || data.sms_skipped || data.sms_skipped_reason === "twilio_not_configured");
        (allGood ? toast.success : toast.warning)(message);
      } else {
        toast.success("Status updated");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline">Bench console</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Admin dashboard</h1>
        </div>
        <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:border-slate-400">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        <Link to="/admin/pricing" data-testid="admin-pricing-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
          <Tag className="w-4 h-4" /> Parts pricing
        </Link>
        <Link to="/admin/settings" data-testid="admin-settings-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:border-slate-400">
          Business settings
        </Link>
        <Link to="/admin/mfa/setup" data-testid="admin-mfa-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold hover:border-slate-400">
          Two-factor
        </Link>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Stat label="Total bookings" value={stats.total} />
            <Stat label="Parts paid" value={stats.paid} tint="amber" />
            <Stat label="In repair" value={stats.in_repair} tint="sky" />
            <Stat label="Ready for pickup" value={stats.ready} tint="emerald" />
            <Stat label="Completed" value={stats.completed} />
          </div>
          {!stats.sms_configured && (
            <div className="mb-6 p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-800 flex items-start gap-2" data-testid="sms-not-configured-notice">
              <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>SMS is off.</strong> Add your Twilio credentials
                (<code className="font-mono">TWILIO_ACCOUNT_SID</code>,{" "}
                <code className="font-mono">TWILIO_AUTH_TOKEN</code>,{" "}
                <code className="font-mono">TWILIO_PHONE_NUMBER</code>) to <code className="font-mono">backend/.env</code> to
                start sending pickup texts. Email is still active.
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          { id: "all", label: "All" },
          { id: "parts_queue", label: "Parts to order" },
          { id: "active", label: "Active repairs" },
          { id: "ready", label: "Ready for pickup" },
        ].map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table data-testid="admin-orders-table" className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Ticket</th>
              <th className="p-3">Device</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Drop-off</th>
              <th className="p-3">Parts / Labor</th>
              <th className="p-3">Paid</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">No bookings in this view.</td></tr>
            )}
            {filtered.map((b) => (
              <tr key={b.id} className={`border-t border-slate-100 align-top hover:bg-slate-50/40 ${b.is_warranty_return ? "bg-emerald-50/40" : ""}`}>
                <td className="p-3 font-mono text-xs font-bold text-slate-900">
                  <Link to={`/admin/bookings/${b.id}`} data-testid={`admin-booking-link-${b.ticket_code}`} className="hover:underline">{b.ticket_code}</Link>
                  {b.is_warranty_return && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`warranty-badge-${b.ticket_code}`}>
                      Warranty
                    </div>
                  )}
                  {b.original_ticket_code && (
                    <div className="mt-1 text-[10px] text-slate-400 font-normal font-sans">on {b.original_ticket_code}</div>
                  )}
                </td>
                <td className="p-3">
                  <div className="font-semibold text-slate-900">{b.brand} {b.model_name}</div>
                  <div className="text-xs text-slate-500 capitalize">{b.device_type}</div>
                  <ul className="mt-1 text-xs text-slate-500 list-disc pl-4">
                    {b.services.map((s) => <li key={s}>{s.replace("_", " ")}</li>)}
                  </ul>
                  {b.photos && b.photos.length > 0 && (
                    <div className="mt-2" data-testid={`admin-photos-${b.ticket_code}`}>
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
                        <Camera className="w-3 h-3" /> {b.photos.length} photo{b.photos.length > 1 ? "s" : ""}
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {b.photos.map((p) => (
                          <a
                            key={p.id}
                            href={`${API}/photos/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-12 h-12 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 block hover:border-amber-500 transition-colors"
                            data-testid={`admin-photo-thumb-${p.id}`}
                          >
                            <img src={`${API}/photos/${p.id}`} alt={p.filename} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <div className="font-medium text-slate-900">{b.customer.name}</div>
                  <div className="text-xs text-slate-500">{b.customer.email}</div>
                  <div className="text-xs text-slate-500">{b.customer.phone}</div>
                </td>
                <td className="p-3 text-slate-700">{b.dropoff_date}<br /><span className="text-xs text-slate-500">{b.dropoff_time}</span></td>
                <td className="p-3 font-mono text-xs">
                  {b.quote ? (
                    <><span className="text-amber-700">${b.quote.parts_total.toFixed(2)}</span> / <span className="text-sky-700">${b.quote.labor_total.toFixed(2)}</span></>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3">
                  {b.parts_paid
                    ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Paid</span>
                    : <span className="text-xs text-slate-400">Pending</span>}
                  <div className="mt-1 flex flex-col gap-0.5">
                    {b.pickup_email_id && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700" data-testid={`admin-email-badge-${b.ticket_code}`}>
                        <Mail className="w-3 h-3" /> Email sent
                      </span>
                    )}
                    {b.pickup_sms_sid && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700" data-testid={`admin-sms-badge-${b.ticket_code}`}>
                        <MessageSquare className="w-3 h-3" /> SMS sent
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <select
                    data-testid={`admin-status-select-${b.ticket_code}`}
                    value={b.status}
                    onChange={(e) => setStatus(b.id, e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right space-y-1">
                  {b.parts_paid && !b.parts_ordered && (
                    <button
                      data-testid={`admin-btn-mark-ordered-${b.ticket_code}`}
                      onClick={() => markOrdered(b.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-500"
                    >
                      <Package className="w-3.5 h-3.5" /> Mark ordered
                    </button>
                  )}
                  {b.status !== "ready_for_pickup" && b.status !== "completed" && (
                    <button
                      data-testid={`admin-btn-mark-repaired-${b.ticket_code}`}
                      onClick={() => setStatus(b.id, "ready_for_pickup")}
                      className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
                    >
                      <Mail className="w-3.5 h-3.5" /> Ready + email
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tint }) {
  const bg = tint === "amber" ? "bg-amber-50 border-amber-200" : tint === "sky" ? "bg-sky-50 border-sky-200" : tint === "emerald" ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200";
  return (
    <div className={`p-4 rounded-xl border ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
